import type { User } from '@shop-monorepo/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../services/api';

export const AUTH_QUERY_KEY = ['auth', 'me'];

export const useMe = () => {
  return useQuery<User, Error>({
    queryKey: AUTH_QUERY_KEY,
    queryFn: () => apiClient.auth.me(),
    enabled: true,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation<{ user: User }, Error, { email: string; password: string }>({
    mutationFn: ({ email, password }) => apiClient.auth.login(email, password),
    onSuccess: ({ user }) => {
      queryClient.setQueryData(AUTH_QUERY_KEY, user);
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return async () => {
    try {
      await apiClient.auth.logout();
    } catch {
      // ignore logout errors; proceed with local cleanup
    }
    queryClient.setQueryData(AUTH_QUERY_KEY, null);
    queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
  };
};

export const useChangePassword = () => {
  return useMutation<
    { success: boolean },
    Error,
    { currentPassword: string; newPassword: string; confirmPassword: string }
  >({
    mutationFn: (data) => apiClient.auth.changePassword(data),
  });
};

export const useForgotPassword = () => {
  return useMutation<{ success: boolean }, Error, { email: string }>({
    mutationFn: ({ email }) => apiClient.auth.forgotPassword(email),
  });
};

export const useResetPassword = () => {
  return useMutation<
    { success: boolean },
    Error,
    { token: string; newPassword: string; confirmPassword: string }
  >({
    mutationFn: (data) => apiClient.auth.resetPassword(data),
  });
};
